import React from 'react';
import { Dialog } from '@headlessui/react';
import { Mic, MessageSquare, Square, Sparkles, Check, X } from 'lucide-react';

const OnboardingModal = ({ open, onComplete, onSkip }) => {
  return (
    <Dialog open={open} onClose={onSkip} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/80" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden my-8 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 transition-colors">
          {/* Header with Gradient */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-700 dark:to-blue-700 text-white p-8 text-center relative transition-colors">
            {/* Close button */}
            <button
              onClick={onSkip}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur hover:bg-white/30 transition-colors flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-5xl">👋</span>
            </div>
            <h2 className="text-3xl font-bold">Welcome to Logly!</h2>
          </div>

          {/* Content */}
          <div className="p-8 bg-white dark:bg-gray-800 transition-colors">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center transition-colors">
              Track time by voice in 5 simple steps:
            </h3>

            <div className="space-y-4 mb-8">
              {[
                { icon: <Mic className="w-6 h-6" />, text: 'Tap the microphone button', color: 'from-purple-500 to-pink-600' },
                { icon: <MessageSquare className="w-6 h-6" />, text: 'Speak naturally: "2 hours on API development"', color: 'from-blue-500 to-cyan-600' },
                { icon: <Square className="w-6 h-6" />, text: 'Tap Stop when done', color: 'from-orange-500 to-red-600' },
                { icon: <Sparkles className="w-6 h-6" />, text: 'AI processes and fills details', color: 'from-green-500 to-teal-600' },
                { icon: <Check className="w-6 h-6" />, text: 'Review and confirm', color: 'from-indigo-500 to-purple-600' }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600">
                  <div className={`w-12 h-12 bg-gradient-to-br ${step.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                    {step.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900 dark:text-gray-100 text-lg transition-colors">{idx + 1}.</span>
                      <span className="text-gray-700 dark:text-gray-300 font-medium transition-colors">{step.text}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-6 rounded-xl border border-cyan-200 dark:border-cyan-800 mb-8 transition-colors">
              <p className="text-center text-gray-700 dark:text-gray-300 transition-colors">
                <span className="font-semibold text-cyan-700 dark:text-cyan-300 transition-colors">💡 Pro Tip:</span> Or start a Quick Timer for common projects
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                onClick={onSkip}
                className="flex-1 py-4 text-gray-600 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors"
              >
                Skip tour
              </button>
              <button
                onClick={() => {
                  // Dispatch event to open Help Modal
                  window.dispatchEvent(new CustomEvent('openHelpModal'));
                  // Then mark onboarding as complete
                  onComplete();
                }}
                className="flex-1 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-700 dark:to-blue-700 text-white font-bold rounded-xl hover:from-cyan-700 hover:to-blue-700 dark:hover:from-cyan-800 dark:hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
              >
                Got it, let's try!
              </button>
            </div>

            {/* Trial Info */}
            <div className="mt-6 text-center">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-full border border-cyan-200 dark:border-cyan-800 transition-colors">
                <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-300 transition-colors">
                  🎉 Your 7-day free trial started! Then just $2.99/month.
                </p>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default OnboardingModal;
